// The Categories tab of Products (docs/plans/categories.md): renaming checks for a name
// already in use, and moving a category's products (then deleting it) is planned here.
import type { SyncOp } from '@ims/core'
import type { ProductRow, RowOf } from '@ims/data'
import { planBulkEdit } from './bulk-edit.ts'
import { deleteOp, updateOp } from './rows.ts'

type CategoryRow = RowOf<'categories'>

/**
 * The other category already called `name` (case and spaces ignored), if any. The server
 * keeps names unique, so a clash would be refused at sync.
 */
export function nameClash<C extends { id: string; name: string }>(
  name: string,
  categories: readonly C[],
  selfId?: string,
): C | null {
  const wanted = name.trim().toLowerCase()
  if (!wanted) return null
  return categories.find((c) => c.id !== selfId && c.name.trim().toLowerCase() === wanted) ?? null
}

export interface CategoryMovePlan {
  /** Commits in order; each one is one sync tx. */
  chunks: SyncOp[][]
  /** Products that change category. */
  moved: number
}

/**
 * Moves every product of a category to `to` (null: no category), whole variant families,
 * 200 a commit. With `remove`, the category's delete goes in the last commit, so it is only
 * deleted once nothing points at it.
 */
export function planCategoryMove(
  categoryId: string,
  products: readonly ProductRow[],
  to: string | null,
  remove: boolean,
  chunkSize = 200,
): CategoryMovePlan {
  const inside = products.filter((p) => p.category_id === categoryId && !p.deleted_at)
  const plan = planBulkEdit(
    inside.map((p) => p.id),
    inside,
    { category_id: to },
    chunkSize,
  )
  const chunks = plan.chunks.map((c) => [...c])
  if (remove) {
    if (chunks.length === 0) chunks.push([])
    chunks[chunks.length - 1]!.push(deleteOp('categories', categoryId))
  }
  return { chunks, moved: plan.changed.length }
}

/** Where a category stands, in a row's note: what is out and low, or that all is in stock. */
export function categoryStanding(c: { count: number; low: number; out: number }): {
  main: string
  tone?: 'danger' | 'warning' | 'success'
} {
  if (c.count === 0) return { main: 'Empty' }
  const parts = [c.out > 0 && `${c.out} out`, c.low > 0 && `${c.low} low`].filter(Boolean)
  if (parts.length === 0) return { main: 'All in stock', tone: 'success' }
  return { main: parts.join(' · '), tone: c.out > 0 ? 'danger' : 'warning' }
}

// A restaurant's categories are its menu's sections: their order is the guest menu's and
// the order screen's (docs/plans/menu-sections.md).

type Ordered = { id: string; name: string; sort_order: number }

/** Categories in menu order: `sort_order`, then name. */
export function menuOrder<C extends Ordered>(categories: readonly C[]): C[] {
  return [...categories].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name))
}

/** The list with `id` moved `delta` places (kept inside the list). */
export function moveInList(ids: readonly string[], id: string, delta: number): string[] {
  const from = ids.indexOf(id)
  if (from < 0) return [...ids]
  const to = Math.max(0, Math.min(ids.length - 1, from + delta))
  const out = [...ids]
  out.splice(from, 1)
  out.splice(to, 0, id)
  return out
}

/** The list with `id` dropped where `before` is (at the end when `before` is null). */
export function dropInList(ids: readonly string[], id: string, before: string | null): string[] {
  const out = ids.filter((x) => x !== id)
  const at = before === null ? out.length : out.indexOf(before)
  out.splice(at < 0 ? out.length : at, 0, id)
  return out
}

/** One commit that numbers the categories 1..n in `ids` order; only rows that change. */
export function reorderOps(categories: readonly CategoryRow[], ids: readonly string[]): SyncOp[] {
  const byId = new Map(categories.map((c) => [c.id, c]))
  return ids.flatMap((id, i) => {
    const c = byId.get(id)
    return c && c.sort_order !== i + 1 ? [updateOp('categories', c, { sort_order: i + 1 })] : []
  })
}

/** Where a new menu category goes: after the last one. */
export function nextSortOrder(categories: readonly { sort_order: number }[]): number {
  return categories.reduce((n, c) => Math.max(n, c.sort_order), 0) + 1
}
